/* ============================================
   Apps — nivel cuenta (/portal/apps)
   Vitrina de apps que ofrecemos + las que el cliente tiene activadas.
   Clic en una app → vista de detalle (/portal/apps/:slug) con cómo
   funciona + consumo de créditos, antes de activar.
   Datos: GET /api/portal/apps. El saldo de créditos vive en el gateway.
   ============================================ */

import { Link } from 'react-router-dom'
import { usePortalCliente } from '@/hooks/usePortalCliente'
import { useCachedData } from '@/hooks/useCachedData'
import { useApi } from '@/hooks/useApi'
import type { PortalAppsResponse, MiApp, AppDisponible, ModeloCobro, EstadoActivacion } from '@/lib/types'
import { Breadcrumbs } from '@/components/ui'

function cobroLabel(mc?: ModeloCobro | null): string {
  if (!mc) return ''
  if (mc.descripcion) return mc.descripcion
  if (mc.tarifas?.length) return mc.tarifas.map(t => `${t.modelo}: ${t.creditos} créd/${t.unidad}`).join(' · ')
  if (mc.creditos) return `${mc.creditos} créditos${mc.unidad ? ` / ${mc.unidad}` : ''}`
  return mc.tipo ?? ''
}

const ESTADO_BADGE: Record<EstadoActivacion, { cls: string; label: string }> = {
  activada: { cls: 'badge-activo', label: 'Activada' },
  pendiente: { cls: 'badge-pausado', label: 'Pendiente de canje' },
  revocada: { cls: 'badge-cancelado', label: 'Revocada' },
}

function inicial(nombre: string): string {
  return (nombre?.trim()?.[0] ?? '?').toUpperCase()
}

function AppLogo({ src, nombre, className }: { src?: string | null; nombre: string; className?: string }) {
  if (src) return <img className={`pa-logo pa-logo--img ${className ?? ''}`} src={src} alt={nombre} />
  return <span className={`pa-logo ${className ?? ''}`}>{inicial(nombre)}</span>
}

function MiAppCard({ app }: { app: MiApp }) {
  const badge = ESTADO_BADGE[app.estado] ?? ESTADO_BADGE.pendiente
  const cobro = cobroLabel(app.modelo_cobro)
  return (
    <Link to={`/portal/apps/${app.app_slug}`} className="pd-card pa-card">
      <div className="pa-card-top">
        <AppLogo src={app.icono_url} nombre={app.nombre ?? app.app_slug} />
        <span className={`badge ${badge.cls}`}>{badge.label}</span>
      </div>
      <span className="pa-card-name">{app.nombre ?? app.app_slug}</span>
      {app.descripcion && <p className="pa-desc">{app.descripcion}</p>}
      <div className="pa-meta">
        <span><strong>{app.asientos}</strong> {app.asientos === 1 ? 'asiento' : 'asientos'}</span>
        {cobro && <span>{cobro}</span>}
      </div>
      <span className="pa-vermas">Ver detalle →</span>
    </Link>
  )
}

function DisponibleCard({ app }: { app: AppDisponible }) {
  const cobro = cobroLabel(app.modelo_cobro)
  return (
    <Link to={`/portal/apps/${app.slug}`} className="pd-card pa-card">
      <div className="pa-card-top">
        <AppLogo src={app.icono_url} nombre={app.nombre} />
      </div>
      <span className="pa-card-name">{app.nombre}</span>
      {app.descripcion && <p className="pa-desc">{app.descripcion}</p>}
      {cobro && <div className="pa-meta"><span>{cobro}</span></div>}
      {app.etiquetas?.length > 0 && (
        <div className="pa-tags">{app.etiquetas.slice(0, 3).map(t => <span key={t} className="badge badge-categoria">{t}</span>)}</div>
      )}
      <span className="pa-vermas">Ver detalle →</span>
    </Link>
  )
}

export default function PortalApps() {
  const { activeClienteId: clienteId } = usePortalCliente()
  const api = useApi()
  const { data = null, isLoading: loading } = useCachedData(
    ['portal-apps', clienteId],
    () => api.get<PortalAppsResponse>(`/api/portal/apps?cliente_id=${clienteId}`)
      .then(r => r ?? { disponibles: [], mis_apps: [] })
      .catch(() => ({ disponibles: [], mis_apps: [] }) as PortalAppsResponse),
    { activo: !!clienteId },
  )

  if (loading) {
    return (
      <div className="portal-loading-state">
        <div className="auth-spinner" />
        <p>Cargando apps...</p>
      </div>
    )
  }

  const misApps = data?.mis_apps ?? []
  const disponibles = data?.disponibles ?? []
  const slugsActivos = new Set(misApps.map(a => a.app_slug))
  const vitrina = disponibles.filter(a => !slugsActivos.has(a.slug))

  return (
    <div className="pd">
      <Breadcrumbs
        items={[{ label: 'Portal', to: '/portal' }, { label: 'Apps' }]}
        meta={misApps.length > 0 ? `${misApps.length} activa${misApps.length === 1 ? '' : 's'}` : undefined}
      />

      {misApps.length > 0 && (
        <section>
          <h2 className="pa-section-title">Mis apps</h2>
          <div className="pa-grid">
            {misApps.map(a => <MiAppCard key={a.id} app={a} />)}
          </div>
        </section>
      )}

      <section>
        <h2 className="pa-section-title">{misApps.length > 0 ? 'Disponibles' : 'Apps disponibles'}</h2>
        {vitrina.length === 0 ? (
          <div className="pd-card"><p className="pa-desc">No hay más apps disponibles por ahora.</p></div>
        ) : (
          <div className="pa-grid">
            {vitrina.map(a => <DisponibleCard key={a.slug} app={a} />)}
          </div>
        )}
      </section>
    </div>
  )
}
